import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Store } from '@ngrx/store';
import { ApiVisa, EntityContainerModel } from 'blg-akaun-ts-lib';
import { SessionActions } from '../../modules/session/session-controller/actions';
import { SessionSelectors } from '../../modules/session/session-controller/selectors';
import { SessionStates } from '../../modules/session/session-controller/states';
import { DropDownService } from '../../services/drop-down-service';
import { Observable, ReplaySubject, Subject, of } from 'rxjs';
import { take, takeUntil, map, switchMap, tap } from 'rxjs/operators';
import { SharedUtilitiesHelperFunctions } from '../helper-functions';

@Component({
  selector: 'app-select-multi-entity-drop-down',
  templateUrl: './select-multi-entity-drop-down.component.html',
  styleUrls: ['./select-multi-entity-drop-down.component.css']
})
export class SelectMultiEntityDropDownComponent implements OnInit, OnDestroy {

  @Input() entity: FormControl;
  @Input() apiVisa: ApiVisa;
  @Input() selectedGuid: string;
  @Input() label = 'Entity';
  @Input() options: any;

  @Output() entitySelected = new EventEmitter<any>();

  serverSideFiltering = new FormControl();
  filteredOptions$: ReplaySubject<any[]> = new ReplaySubject<any[]>(1);
  selectedRow: any;
  rowData: any = [];
  key = "entity";

  allSelected = false;
  filteredCache: any = [];
  compTargets: string[]=[];

  isIndeterminate = false;
  isChecked = false;

  protected _onDestroy = new Subject<void>();

  constructor(private dropDownService: DropDownService, 
    private readonly store: Store<SessionStates>
  ) { 
    
  }

  ngOnInit() {
    // set initial selection
    this.key = this.options['entity'] ?? 'entity';

    this.store.select(SessionSelectors.selectDropDownRowData).subscribe(resolved => {
      this.rowData = resolved?resolved[this.key]?resolved[this.key]:[]:[];
      if (this.rowData.length === 0) {
        //this.getRowData();
      }
      else {
        this.filteredOptions$.next(this.rowData.slice());
      }
    })

    if (this.entity.value) {
      this.setDefaultValue();
    }
    
    // listen for search field value changes
    this.serverSideFiltering.valueChanges
      .pipe(takeUntil(this._onDestroy))
      .subscribe(() => {
        this.filtering();
      });
  }

  ngOnChanges() {
    // console.log('ngOnChanges');
  }

  getRowData(keyword?): Observable<any> {
    if (!this.key) return of([]); // Return an empty observable if key is missing
  
    let formData = {
      "limit": 50,
      "orderBy": "name",
      "order": "ASC",
      "keyword": keyword || ''
    };
  
    if (this.key === 'entity') {
      formData['customColumns']=['guid','name','customer_code','supplier_code','employee_code','merchant_code'];
    }
    return this.dropDownService.get(this.key, formData, this.apiVisa).pipe(
      map(resolved => {
        // Process and return data as needed
        //let rowData = [...this.rowData, ...resolved.data];
        return resolved.data;
      })
    );
  }

  getRowDataByCode(keyword?): Observable<any> {
    if (!this.key) return of([]); // Return an empty observable if key is missing
  
    let formData = {
      "limit": 50,
      "orderBy": "name",
      "order": "ASC",
      "keyword": "",
      "filterLogical": "OR"
    }

    if (this.key === 'entity') {
      formData['customColumns']=['guid','name','customer_code','supplier_code','employee_code','merchant_code'];
      formData['filterConditions'] = [
        {
          "filterColumn": "customer_code",
          "filterValues": [keyword],
          "filterOperator": "IN"
        },
        {
          "filterColumn": "supplier_code",
          "filterValues": [keyword],
          "filterOperator": "IN"
        },
        {
          "filterColumn": "employee_code",
          "filterValues": [keyword],
          "filterOperator": "IN"
        },
        {
          "filterColumn": "merchant_code",
          "filterValues": [keyword],
          "filterOperator": "IN"
        },
      ];
    }
    else {
      let code = '';
      if (this.key === 'customer')
        code = "customer_code";
      else if (this.key === 'supplier')
        code = "supplier_code";
      else if (this.key === 'employee')
        code = "employee_code";
      else if (this.key === 'merchant')
        code = "merchant_code";
      formData['filterConditions'] = [{
        "filterColumn": code,
        "filterValues": [keyword],
        "filterOperator": "IN"
      }];
    }

    return this.dropDownService.get(this.key, formData, this.apiVisa);
  }

  filtering() {
    // if (!this.rowData) {
    //   return;
    // }
  
    let search = this.serverSideFiltering.value;
    if (!search || search.length < 3) {
      return;
    }
  
    search = search?.toLowerCase();
  
    this.getRowDataByCode(search).pipe(
      switchMap(byCodeData => {
        return this.getRowData(search).pipe(
          map(byNameData => {
            byNameData = byNameData
              .filter(row => row.name && row.name.trim() !== '')
              .sort((a, b) => a.name.localeCompare(b.name));
  
            const combinedData = [...byCodeData.data, ...byNameData];
            return combinedData;
          })
        );
      })
    ).subscribe({
      next: combinedData => {
        if (this.isShowAll() && !this.isMultiple()) {
          let emptyOption = { guid: 0, name: "ALL" };
          combinedData.unshift(emptyOption);
        }
        combinedData = SharedUtilitiesHelperFunctions.removeDuplicate(combinedData, 'guid');

        this.rowData = [...this.rowData, ...combinedData];
        this.rowData = SharedUtilitiesHelperFunctions.removeDuplicate(this.rowData, 'guid');
        //console.log(this.rowData);

        // sorting
        //const sorted = this.rowData.sort((a, b) => (a.name < b.name ? -1 : 1));
  
        this.store.dispatch(SessionActions.selectDropDownRowData({key: this.key, rowData: this.rowData}));
        
        //fill the drop down
        if (search) {
          this.filteredOptions$.next(combinedData.slice());
        }
      },
      error: err => {
        console.error('Error fetching data:', err);
      }
    });
  }
  
  onSelect(guid: string) {
    if (guid) {
      this.entity.patchValue(guid);
      this.selectedRow = this.rowData.find(row => row.guid === guid);
      this.entitySelected.emit(this.selectedRow);
    }
  }

  isRequired() {
    let value = this.options['required'];
    if (value === true || value === false) {
      return value;
    }
    else if (value) {
      return value.includes(true);
    }
    return false; 
  }

  isMultiple() {
    let value = this.options['multiple'];
    if (value === true || value === false) {
      return value;
    }
    else if (value) {
      return value.includes(true);
    }
    return false; 
  }

  isShowAll() {
    let value = this.options['showAll'];
    if (value === true || value === false) {
      return value;
    }
    else if (value) {
      return value.includes(true);
    }
    return false; 
  }

  displayRow(obj, name) {
    let code = '';
    if (this.key === 'customer')
      code = obj.customer_code;
    else if (this.key === 'supplier')
      code = obj.supplier_code;
    else if (this.key === 'employee')
      code = obj.employee_code;
    else if (this.key === 'merchant')
      code = obj.merchant_code;
    else 
      code = obj.customer_code ?? obj.supplier_code ?? obj.employee_code ?? obj.merchant_code ?? '';
    return code + (name? ' | ' + name : ''); 
  }

  toggleSelectAll(selectAllValue: boolean) {
    this.filteredOptions$.pipe(take(1), takeUntil(this._onDestroy))
      .subscribe(val => {
        if (selectAllValue) {
          this.entity.patchValue([...val.map(item => item.guid)]);
        } else {
          this.entity.patchValue([]);
        }
      });
  }

  setToggleAllCheckboxState() {
    if (!this.isMultiple()) return;
    let filteredLength = 0;
    if (this.entity && this.entity.value) {
      this.filteredCache.forEach(el => {
        if (this.entity.value.indexOf(el) > -1) {
          filteredLength++;
        }
      });
      this.isIndeterminate = filteredLength > 0 && filteredLength < this.filteredCache.length;
      this.isChecked = filteredLength > 0 && filteredLength === this.filteredCache.length;
    }
  }


  getRowDataByGuids(guids): Observable<any> {
    let formData = {
      "limit": null,
      "orderBy": "name",
      "order": "ASC",
      "keyword": "",
      "filterLogical": "OR"
    }

    if (this.key === 'entity') {
      formData['customColumns'] = ['guid', 'name', 'customer_code', 'supplier_code', 'employee_code', 'merchant_code'];
    }
    else {
      let code = '';
      if (this.key === 'customer')
        code = "customer_code";
      else if (this.key === 'supplier')
        code = "supplier_code";
      else if (this.key === 'employee')
        code = "employee_code";
      else if (this.key === 'merchant')
        code = "merchant_code";
    }

    formData['filterConditions'] = [{
      "filterColumn": "guid",
      "filterValues": guids,
      "filterOperator": "IN"
    }];
    return this.dropDownService.get(this.key, formData, this.apiVisa);
  }

  setDefaultValue() {
    if (Array.isArray(this.entity.value) && this.entity.value.length > 0) {
      const selectedRows = this.rowData.filter(row => this.entity.value.includes(row.guid));

      if (selectedRows && selectedRows.length === 0) {
        this.getRowDataByGuids(this.entity.value).subscribe(data => {
          this.rowData = [...this.rowData, ...data.data];
          this.rowData = SharedUtilitiesHelperFunctions.removeDuplicate(this.rowData, 'guid');
          this.store.dispatch(SessionActions.selectDropDownRowData({ key: this.key, rowData: this.rowData }));
          this.filteredOptions$.next(this.rowData.slice());
        })
      }
    }
  }

  ngOnDestroy() {
    this._onDestroy.next();
    this._onDestroy.complete();
  }

}
